import { create, StateCreator } from "zustand";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { immer } from "zustand/middleware/immer";
import { IVendorUser } from "@smartrr/shared/entities/VendorUser";

export interface MigrationFileUpload {
  loading: boolean;
  uploaded: boolean;
  error: string | null;
  user: IVendorUser | null;

  actions: {
    uploadFile(uploadedFileId: string): Promise<void>;
    setUser(user: IVendorUser | null): void;
  };
}

const migrationTableSlice: StateCreator<MigrationFileUpload, [["zustand/immer", never]]> = (set, get) => ({
  loading: false,
  error: null,
  uploaded: false,
  user: null,
  actions: {
    async uploadFile(uploadedFileId) {
      set(draft => {
        draft.error = "";
      });

      const user = get().user;

      if (!user) {
        return;
      }

      const response = await typedFrontendVendorApi.postReq("/migrations/upload-file", {
        reqBody: {
          uploadedBy: user.originalEmail,
          uploadedFileId,
        },
      });

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Upload failed.";
        });
        return;
      }

      set(draft => {
        draft.uploaded = true;
      });
    },

    setUser(user) {
      if (user) {
        set(draft => {
          draft.user = user;
        });
      }
    },
  },
});

const useMigrationFileUpload = create<MigrationFileUpload>()(immer(migrationTableSlice));

const initialStoreState = useMigrationFileUpload.getState();

export const MigrationFileUploadAccess = {
  useActions() {
    return useMigrationFileUpload(state => state.actions);
  },

  useLoading() {
    return useMigrationFileUpload(state => state.loading);
  },

  useError() {
    return useMigrationFileUpload(state => state.error);
  },

  testing: {
    store: useMigrationFileUpload,
    actions() {
      return useMigrationFileUpload.getState().actions;
    },
    reset() {
      return useMigrationFileUpload.setState(initialStoreState);
    },
  },
};
